import { useEffect, useRef, useState } from 'react';
import './Carousel.css';
import './index.css';

import { Directions, GameState, isCompleted } from './State.tsx';

import ShuffleIcon from './assets/shuffle_resizable.svg';
import ArrowBack from './assets/arrow_back.svg';
import ArrowForward from './assets/arrow_forward.svg';
import DotEmpty from './assets/dot_empty.svg';
import DotFull from './assets/dot_full.svg';
import { mod, MODAL_CONTENT_DEFAULTS, MODAL_OVERLAY_DEFAULTS } from './utils.ts';
import Modal from 'react-modal';
import { CloseButton } from './CloseButton.tsx';
import { track } from './umami.ts';

function Carousel({
    newIndex,
    onSwipe,
    syncedWithParent,
    gameState,
    disabled,
    disableAdvancedCarouselOptions,
    children,
    onOrderClues,
}) {
    const [oldIndex, setOldIndex] = useState(newIndex);
    const [allCluesVisible, setAllCluesVisible] = useState(false);

    const animationInProgress = useRef(false);

    const MIN_SWIPE_DISTANCE_PX = 10;
    const ANIMATION_DURATION_MS = 300;

    const lastIndexChange = useRef(null);
    const dragStart = useRef(null);

    function handleMouseDown(event: React.MouseEvent) {
        handleSwipeStart(event, event);
    }

    function handleTouchStart(event: React.TouchEvent) {
        handleSwipeStart(event.touches.item(0), event);
    }

    // prettier-ignore
    function handleMouseUp(event: React.MouseEvent) { // eslint-disable-line react-hooks/exhaustive-deps
        handleSwipeEnd(event, event);
    }

    // prettier-ignore
    function handleTouchEnd(event: React.TouchEvent) { // eslint-disable-line react-hooks/exhaustive-deps
        handleSwipeEnd(event.changedTouches.item(0), event);
    }

    function handleSwipeStart(
        swipeStart: React.MouseEvent | React.Touch,
        rawEvent: React.MouseEvent | React.TouchEvent
    ) {
        if (dragStart.current) return;
        dragStart.current = [swipeStart.clientX, swipeStart.clientY];
        rawEvent.stopPropagation();
    }

    function handleSwipeEnd(swipeEnd: React.MouseEvent | React.Touch, rawEvent: React.MouseEvent | React.TouchEvent) {
        if (!dragStart.current) return;
        const swipeMovement = swipeEnd.clientX - dragStart.current[0];
        dragStart.current = null;
        const swipeDistance = Math.abs(swipeMovement);
        if (swipeDistance < MIN_SWIPE_DISTANCE_PX) {
            return;
        }
        const swipedRight = swipeMovement > 0;
        if (swipedRight) {
            handleSwipeRight();
        } else {
            handleSwipeLeft();
        }
        rawEvent.stopPropagation();
    }

    function handleSwipeRight() {
        if (disabled) return;
        if (animationInProgress.current) return;
        onSwipe(leftIndex(oldIndex));
    }

    function handleSwipeLeft() {
        if (disabled) return;
        if (animationInProgress.current) return;
        onSwipe(rightIndex(oldIndex));
    }

    // prettier-ignore
    function handleKeyDown(event: KeyboardEvent) { // eslint-disable-line react-hooks/exhaustive-deps
        if (event.key === 'ArrowLeft') {
            handleSwipeRight();
        } else if (event.key === 'ArrowRight') {
            handleSwipeLeft();
        }
    }

    useEffect(() => {
        window.addEventListener('touchend', handleTouchEnd);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('touchend', handleTouchEnd);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleMouseUp, handleTouchEnd, handleKeyDown, newIndex]);

    function leftIndex(idx: number) {
        return mod(idx - 1, children.length);
    }

    function rightIndex(idx: number) {
        return mod(idx + 1, children.length);
    }

    function getCarouselItems() {
        // Find the first non-one-time element to the left of the current index.
        return [children[leftIndex(oldIndex)], children[oldIndex], children[rightIndex(oldIndex)]];
    }

    function getEffectiveIndex(idx: number) {
        return idx;
    }

    function getDirection() {
        if (oldIndex === newIndex) {
            return Directions.NONE;
        } else if (oldIndex > newIndex) {
            if (Math.abs(oldIndex - newIndex) < Math.abs(newIndex + children.length - oldIndex)) {
                // [ index, oldIndex, index + N ]
                // If the distance to the left is shorter than the distance to the right, go left.
                return Directions.LEFT;
            } else {
                return Directions.RIGHT;
            }
        } else {
            // [ index - N, oldIndex, index ]
            if (Math.abs(oldIndex - newIndex) < Math.abs(newIndex - children.length - oldIndex)) {
                // If the distance to the right is shorter than the distance to the left, go right.
                return Directions.RIGHT;
            } else {
                return Directions.LEFT;
            }
        }
    }

    useEffect(() => {
        lastIndexChange.current = Date.now();
        setTimeout(() => {
            setOldIndex(newIndex);
        }, ANIMATION_DURATION_MS);
    }, [newIndex]);

    function closeAllCluesModal() {
        setAllCluesVisible(false);
    }

    function onShuffleClicked() {
        if (disableAdvancedCarouselOptions) {
            alert("This button will order the clues, but it's disabled during this tour.");
        } else {
            onOrderClues();
        }
    }

    function onDotsClicked() {
        if (disableAdvancedCarouselOptions) {
            alert("This button will show all clues at once during gameplay, but it's disabled during this tour.");
        } else {
            track('view_all_clues');
            setAllCluesVisible(true);
        }
    }

    return (
        <div className='carousel-container'>
            <div className='pagination-container'>
                <div
                    className='pagination-arrow-container pagination-arrow-container-left cursor-pointer'
                    onClick={handleSwipeRight}
                    data-testid='carousel-left-arrow'>
                    <ArrowBack className={`pagination-arrow ${disabled ? 'pagination-arrow-disabled' : ''}`} />
                </div>
                <div className='carousel-view-area'>
                    <div
                        className='carousel'
                        data-testid='carousel'
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        style={{
                            transition: `transform ${
                                newIndex === oldIndex || Date.now() - lastIndexChange.current < ANIMATION_DURATION_MS
                                    ? '0s'
                                    : `${ANIMATION_DURATION_MS / 1000}s`
                            } linear 0s`,
                            transform: `translateX(${-33.3333 * (1 + getDirection())}%)`,
                        }}>
                        {getCarouselItems()}
                    </div>
                </div>

                <div
                    className='pagination-arrow-container pagination-arrow-container-right cursor-pointer'
                    onClick={handleSwipeLeft}
                    data-testid='carousel-right-arrow'>
                    <ArrowForward className={`pagination-arrow ${disabled ? 'pagination-arrow-disabled' : ''}`} />
                </div>
            </div>

            <div className='indicator-container'>
                {!syncedWithParent && (
                    <div
                        className='shuffle-icon-container cursor-pointer'
                        data-testid='shuffle-icon-container'
                        onClick={onShuffleClicked}>
                        <ShuffleIcon className='shuffle-icon' />
                    </div>
                )}
                <div className='dot-container cursor-pointer' data-testid='dot-container' onClick={onDotsClicked}>
                    {Array.from({ length: 3 }).map((_, i) =>
                        i === getEffectiveIndex(newIndex) ? (
                            <DotFull
                                key={i}
                                className={`dot ${syncedWithParent ? 'dot-synced' : ''} ${isCompleted(
                                    gameState
                                )} ? 'dot-solved'`}
                            />
                        ) : (
                            <DotEmpty key={i} className={`dot ${syncedWithParent ? 'dot-synced' : ''}`} />
                        )
                    )}
                </div>
            </div>
            <Modal
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        width: '260px',
                        maxWidth: 'var(--modal-max-width)',
                        height: 'fit-content',
                        border: '2px solid var(--default-text-color)',
                        borderRadius: '1px',
                        margin: '0 auto',
                        backgroundColor: 'var(--body-background-color)',
                        color: 'var(--default-text-color)',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}
                overlayClassName='centered-modal'
                onRequestClose={closeAllCluesModal}
                isOpen={allCluesVisible}>
                <CloseButton onClose={closeAllCluesModal} />
                <div className='all-clues-modal' data-testid='all-clues-modal'>
                    {children.map((child, idx) => {
                        return (
                            <div className={`${idx === oldIndex ? 'active-clue' : ''} modal-clue-container`} key={idx}>
                                {child}
                            </div>
                        );
                    })}
                    {!syncedWithParent && (
                        <div className='shuffle-icon-container' data-testid='shuffle-icon-container'>
                            <ShuffleIcon className='shuffle-icon' />
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
}

function CarouselItem({ className = '', children }) {
    return <div className={`carousel-item ${className}`}>{children}</div>;
}

export { Carousel, CarouselItem };
